import { useMutation, useQuery } from "@tanstack/react-query";
import { BusOutline24Icon } from "@vygruppen/spor-icon-react";
import { postAlternativeTransportLog } from "api/alternativeTransportLogs";
import { getBackendUrl } from "api/common";
import { usePatchRequest } from "api/http/hooks";
import { isFailureRequest, isSucessOrFailure } from "api/http/httpRequest";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { PanelAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import { SelectReserveVehicleDropdown } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleReplacement/SelectReserveDropdown";
import {
  getDetailedReplacementLogMessage,
  getLogMessageForReserveVehicle,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleReplacement/utils";
import { FormEvent, useEffect, useState } from "react";
import { ActionModal } from "shared/components/ActionModal";
import { tokens, VStack } from "shared/components/Spor";
import {
  AlternativeTransportLogReq,
  AlternativeTransportVehicle,
  GetReserveAlternativeTransportResponse,
  ReserveAlternativeTransportBookingRequest,
  reserveAlternativeTransportBookingRequestSchema,
  StopReference,
  UpdateAlternativeTransportRequest,
  updateAlternativeTransportRequestSchema,
} from "shared/types/alternativeTransport";
import { getISODrivingFromToTimes } from "shared/utils/dateTimeUtils";
import { css, useTheme } from "styled-components";

type Props = {
  vehicle: AlternativeTransportVehicle;
  distributionStopReferences: Array<StopReference>;
};

/**
 * Replacement action descriptor. Always available.
 */
export const useVehicleReplacementAction = ({
  vehicle,
  distributionStopReferences,
}: Props): PanelAction => {
  const theme = useTheme();

  const [showModal, setShowModal] = useState(false);
  const [selectedReserveId, setSelectedReserveId] = useState<string>("");
  const { from, to } = getISODrivingFromToTimes(
    vehicle.scheduledDepartureTimeOrigin,
  );

  const distributionStopPlaces = distributionStopReferences
    .filter((stop) => !!stop.nsrStopPlace)
    .map((stop) => stop.nsrStopPlace)
    .join(",");

  const searchParams = new URLSearchParams({ from, to, countryCode: "NO" });

  const {
    data: reserveVehicles,
    refetch: fetchReserveVehicles,
    isFetching: isReserveLoading,
    isError: isReserveError,
  } = useQuery({
    queryKey: ["getReserveVehicleReplacement", from, to],
    refetchOnWindowFocus: false,
    enabled: false,
    queryFn: ({ signal }) =>
      queryFnGET<GetReserveAlternativeTransportResponse>({
        signal,
        url: `${getBackendUrl()}/alternativeTransports/reserve?${searchParams.toString()}`,
      }),
  });

  const {
    patchRequestStatus: updateVehicleRequestStatus,
    patchRequest: updateVehicleRequest,
    resetStatus: resetUpdateVehicleRequest,
  } = usePatchRequest<any, UpdateAlternativeTransportRequest>(
    `${getBackendUrl()}/alternativeTransports/${vehicle.id}`,
  );

  const {
    patchRequestStatus: bookRequestStatus,
    patchRequest: bookVehicleRequest,
    resetStatus: resetBookVehicleRequest,
  } = usePatchRequest<any, ReserveAlternativeTransportBookingRequest>(
    `${getBackendUrl()}/alternativeTransports/reserve/${selectedReserveId}/book`,
  );

  const {
    mutate: postVehicleLog,
    status: logRequestStatus,
    reset: resetLogRequest,
  } = useMutation({
    mutationKey: ["postVehicleLog", vehicle.id],
    mutationFn: (data: AlternativeTransportLogReq) =>
      postAlternativeTransportLog(vehicle.id, data),
  });

  const selectedReserve =
    reserveVehicles?.vehicles.find(
      (reserveVehicle) => reserveVehicle.id === selectedReserveId,
    ) ?? undefined;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (selectedReserve) {
      const payload: UpdateAlternativeTransportRequest = {
        phoneNumberUpdateRequest: {
          phoneNumber: selectedReserve.driverPhoneNumber ?? "",
          distributionStopReferences,
        },
        operatorNameUpdateRequest: {
          operatorId: selectedReserve.operatorId ?? "",
          operatorName: selectedReserve.operatorName ?? "",
          distributionStopReferences,
        },
      };

      bookVehicleRequest(
        reserveAlternativeTransportBookingRequestSchema.parse({
          available: false,
        }),
      );
      updateVehicleRequest(
        updateAlternativeTransportRequestSchema.parse(payload),
      );
    }
  };

  useEffect(() => {
    if (
      selectedReserve &&
      isSucessOrFailure(bookRequestStatus) &&
      isSucessOrFailure(updateVehicleRequestStatus)
    ) {
      const logMessage = getDetailedReplacementLogMessage(
        bookRequestStatus,
        updateVehicleRequestStatus,
        vehicle,
        selectedReserve,
      );

      if (logMessage) {
        postVehicleLog({
          message: logMessage,
          distributionStopPlaces,
          alternativeTransportId: vehicle.id,
        });

        const logMessageForReserveAlternativeTransport =
          getLogMessageForReserveVehicle(vehicle);

        postVehicleLog({
          message: logMessageForReserveAlternativeTransport,
          distributionStopPlaces,
          alternativeTransportId: selectedReserve.id,
        });

        resetUpdateVehicleRequest();
        resetBookVehicleRequest();
      }
    }
  }, [updateVehicleRequestStatus, bookRequestStatus, selectedReserve]);

  const onOpen = () => {
    void fetchReserveVehicles();
    setShowModal(true);
  };

  const onClose = () => {
    setShowModal(false);
    resetUpdateVehicleRequest();
    resetBookVehicleRequest();
    resetLogRequest();
    setSelectedReserveId("");
  };

  const isOverallError =
    [updateVehicleRequestStatus, bookRequestStatus].some((status) =>
      isFailureRequest(status),
    ) || logRequestStatus === "error";

  return {
    key: "replace",
    label: "Erstatt",
    leftIcon: <BusOutline24Icon />,
    buttonVariant: "tertiary",
    buttonSize: "sm",
    buttonTitle: "Erstatt planlagt tur med et reservekjøretøy",
    onSelect: onOpen,
    modal: showModal ? (
      <ActionModal
        title="Erstatt avgang med reserve"
        subtitle="Velg reservekjøretøy:"
        actionTitle="Erstatt"
        noScrollBar
        onClose={onClose}
        onSubmit={onSubmit}
        loading={logRequestStatus === "pending"}
        isSuccess={logRequestStatus === "success"}
        isError={isOverallError}
        isSubmitDisabled={selectedReserveId.length === 0}
        successMessage="Reserve satt inn"
        failureMessage="Kunne ikke erstatte avgang. Prøv igjen, eller kontakt IT hvis feilen vedvarer."
        contentBodyStyle={css`
          background-color: ${theme.colorBackgroundSecondary};
          border-radius: ${tokens.default.size["border-radius"].md};
        `}
      >
        <VStack w="100%" alignItems="stretch">
          <SelectReserveVehicleDropdown
            reserveVehicles={reserveVehicles}
            isReserveLoading={isReserveLoading}
            isReserveError={isReserveError}
            selectedReserveId={selectedReserveId}
            setSelectedReserveId={setSelectedReserveId}
          />
        </VStack>
      </ActionModal>
    ) : null,
  };
};
